/*
 * Copyright (c) 2022-2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {
  BrowserModule,
  HAMMER_GESTURE_CONFIG,
  HammerGestureConfig,
} from "@angular/platform-browser";
import { NgModule, Injectable } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { CheckboxModule } from "primeng/checkbox";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
import { MatMenuModule } from "@angular/material/menu";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatSliderModule } from "@angular/material/slider";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatSortModule } from "@angular/material/sort";
import { MatTableModule } from "@angular/material/table";
import { MatTabsModule } from "@angular/material/tabs";
import { MatToolbarModule } from "@angular/material/toolbar";
import { ToolbarComponent } from "./components/toolbar/toolbar.component";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDialogModule } from "@angular/material/dialog";
import { MatSelectModule } from "@angular/material/select";
import { MatChipsModule } from "@angular/material/chips";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { ReactiveFormsModule } from "@angular/forms";
import { VehicleComponent } from "./components/vehicle/vehicle.component";
import { MatCardModule } from "@angular/material/card";
import { ExcelExportDirective } from "./directive/excel-export.directive";
import { CompetitorVehicleComponent } from "./components/reports/competitor-vehicle/competitor-vehicle.component";
import { ImageGalleryComponent } from "./components/image-gallery/image-gallery.component";
import { NgxGalleryModule } from "@kolkov/ngx-gallery";
import { HomepageComponent } from "./components/homepage/homepage.component";
import { ErrorHandlerDialogComponent } from "./components/error-handler-dialog/error-handler-dialog.component";

import { VehicleRolloutComponent } from "./components/reports/vehicle-rollout/vehicle-rollout.component";
import { PropulsionRolloutComponent } from "./components/reports/propulsion-rollout/propulsion-rollout.component";
import { AuthInterceptor } from "./components/utility/authinterceptor";
import { ReferenceTableComponent } from "./components/reference-table/reference-table.component";
import { ReferenceTableDialogComponent } from "./components/reference-table/reference-table-dialog/reference-table-dialog.component";
import { NewVehicleComponent } from "./components/new-vehicle/new-vehicle.component";
import { NewBodycycleComponent } from "./components/new-bodycycle/new-bodycycle.component";
import { NewPropulsionComponent } from "./components/new-propulsion/new-propulsion.component";
import { VehicleSubrecordsComponent } from "./components/vehicle-subrecords/vehicle-subrecords.component";
import { VehicleVersionComponent } from "./components/vehicle-version/vehicle-version.component";
import { DocumentComponent } from "./components/document/document.component";
import { CompetitiveActivityLandscapeComponent } from "./components/reports/competitive-activity-landscape/competitive-activity-landscape.component";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { FabMultiselectModule } from "@fabric/components/multiselect";
import { HomepageEditorComponent } from "./components/homepage/homepage-editor/homepage-editor.component";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { ConfirmChangesDialogComponent } from "./components/homepage/confirm-changes-dialog/confirm-changes-dialog.component";
import { AuthenticationModule } from "./components/authentication/authentication/authentication.module";
import { OAuthStorage } from "angular-oauth2-oidc";
import { ConfirmDeletionDialogComponent } from "./components/utility/confirm-deletion-dialog/confirm-deletion-dialog.component";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { SelectionSortComponent } from "./components/base-components/selection-sort/selection-sort.component";
import { QuillModule } from "ngx-quill";
import { CommonModule } from "@angular/common";
import { OAuthService } from "angular-oauth2-oidc";
import { providePrimeNG } from "primeng/config";
import { definePreset } from "@primeng/themes";
import Lara from "@primeng/themes/lara";

const cpdPrimeNgPreset = definePreset(Lara, {
  semantic: {
    primary: {
      50: "{blue.50}",
      100: "{blue.100}",
      200: "{blue.200}",
      300: "{blue.300}",
      400: "{blue.400}",
      500: "{blue.500}",
      600: "{blue.600}",
      700: "{blue.700}",
      800: "{blue.800}",
      900: "{blue.900}",
      950: "{blue.950}",
    },
  },
});
import { TableModule } from "primeng/table";
import { ButtonModule } from "primeng/button";
import { ClickableLinkPipe } from "./pipes/clickable-link.pipe";
import { MultiSelectModule } from "primeng/multiselect";
import { DatePickerModule } from "primeng/datepicker";
import { FloatLabelModule } from "primeng/floatlabel";
import { InputTextModule } from "primeng/inputtext";
import { SelectModule } from "primeng/select";
import { PaginatorModule } from "primeng/paginator";
import { AnnouncementEditorComponent } from "./components/homepage/announcement-editor/announcement-editor.component";
import { EditableTableComponent } from "./components/editable-table/editable-table.component";
import { VehicleVersionReportComponent } from "./components/reports/vehicle-version-report/vehicle-version-report.component";

@Injectable()
export class CustomHammerConfig extends HammerGestureConfig {
  overrides = {
    pinch: { enable: false },
    rotate: { enable: false },
  };
}

export function storageFactory(): OAuthStorage {
  return localStorage;
}

@NgModule({ declarations: [
        AppComponent,
        ToolbarComponent,
        VehicleComponent,
        ExcelExportDirective,
        CompetitorVehicleComponent,
        ImageGalleryComponent,
        HomepageComponent,
        ErrorHandlerDialogComponent,
        VehicleRolloutComponent,
        PropulsionRolloutComponent,
        ReferenceTableComponent,
        ReferenceTableDialogComponent,
        NewVehicleComponent,
        NewBodycycleComponent,
        NewPropulsionComponent,
        VehicleSubrecordsComponent,
        VehicleVersionComponent,
        DocumentComponent,
        CompetitiveActivityLandscapeComponent,
        HomepageEditorComponent,
        ConfirmChangesDialogComponent,
        ConfirmDeletionDialogComponent,
        SelectionSortComponent,
        ClickableLinkPipe,
        AnnouncementEditorComponent,
        EditableTableComponent,
        VehicleVersionReportComponent,
    ],
    bootstrap: [AppComponent], imports: [AuthenticationModule,
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        MatToolbarModule,
        MatSidenavModule,
        MatListModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatFormFieldModule,
        MatInputModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        MatDialogModule,
        MatSelectModule,
        MatChipsModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        MatCardModule,
        MatGridListModule,
        FormsModule,
        NgxGalleryModule,
        CheckboxModule,
        MatCheckboxModule,
        MatSnackBarModule,
        MatSliderModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatTabsModule,
        FabMultiselectModule,
        MatInputModule,
        DragDropModule,
        ScrollingModule,
        QuillModule.forRoot(),
        CommonModule,
        MultiSelectModule,
        DatePickerModule,
        FloatLabelModule,
        InputTextModule,
        SelectModule,
        TableModule,
        ButtonModule,
        PaginatorModule], providers: [
        { provide: HAMMER_GESTURE_CONFIG, useClass: CustomHammerConfig },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: OAuthStorage, useFactory: storageFactory },
        { provide: OAuthService, useClass: OAuthService },
        provideHttpClient(withInterceptorsFromDi()),
        providePrimeNG({
            theme: {
                preset: cpdPrimeNgPreset,
                options: {
                    darkModeSelector: false,
                },
            },
            ripple: true,
        }),
    ] })
export class AppModule {}
